// ChatWidgetBody (component)
import React, { useState, useRef, useEffect, useCallback } from 'react';
import ChatWidgetInput from './ChatWidgetInput/ChatWidgetInput';
import Message from './Message/Message';
import TypingIndicator from './Message/TypingIndicator/TypingIndicator';
import {
    GroupMessagesPayload,
    IChatBannerData,
    IMessage,
    IStructuredCapableResponse,
    IStructuredMessageSection,
    IStructuredProgressPayload,
    IStructuredProgressPayloadItem,
    IStructuredProgressSection,
    IStructuredSectionPayload,
    IStructuredTablePayload,
    IStructuredTablePayloadCell,
    IStructuredTablePayloadRow,
    IStructuredTablePayloadRowObject,
    IStructuredTablePayloadSummary,
    IStructuredTableSection
} from 'services/interface';
import { chatService } from 'services/chat.service';
import { apiRoutes } from 'services/routes';
import { authService } from 'services/auth.service';
import { createUUID } from 'utils/helper';
import { useWebSocket } from 'hooks/useWebSocket';
import { useTracing } from 'hooks/useTracing';
import { useLanguage } from 'contexts/LanguageContext';
import { ToastProvider } from 'contexts/ToastContext';
import ToastContainer from 'components/utils/Toast/ToastContainer';
import { webSocketService } from 'services/websocket.service';
import {
    getUrlParam,
    getChatSource,
    isAppChannelParam,
    isAppIntegrationRoute,
    isEmbedRoute,
    persistChatSource,
    splitName
} from 'utils/embedParams';
import { notifyEmbedParent } from 'utils/embedMessaging';
import {
    VIEW_BILL_DEMO_ACTION,
    VIEW_BILL_DEMO_GRID_ID_PREFIX,
    VIEW_BILL_DEMO_SHARE_ACTION,
    createViewBillDemoDocumentMessages,
    createViewBillDemoGridMessage,
    createViewBillDemoShareMessage
} from './viewBillDemo';
import {
    consumeChatSessionExpiredNotice,
    getInactivityReminderText,
    INACTIVITY_CANCEL_ACTION,
    INACTIVITY_CONTINUE_ACTION,
    INACTIVITY_REMINDER_ID_PREFIX,
    INACTIVITY_REMINDER_MS,
    markChatSessionExpired,
    SESSION_EXPIRED_NOTICE_ID_PREFIX,
    SESSION_EXPIRED_RESTART_TEXT,
    SESSION_TIMEOUT_MS
} from 'utils/chatSessionTimeout';
import { imageLinks } from 'assets/images';
import "./ChatWidgetBody.scss";


interface UserMetadataInterface {
    firstName: string;
    lastName: string;
    phone: string;
}

const isInactivityPromptMessage = (message: IMessage) =>
    message.id.startsWith(INACTIVITY_REMINDER_ID_PREFIX) ||
    (message.actions?.some(action =>
        action.action === INACTIVITY_CONTINUE_ACTION ||
        action.action === INACTIVITY_CANCEL_ACTION
    ) ?? false);

const isSessionExpiredNoticeMessage = (message: IMessage) =>
    message.id.startsWith(SESSION_EXPIRED_NOTICE_ID_PREFIX);

const isSystemIdleMessage = (message: IMessage) =>
    isInactivityPromptMessage(message) || isSessionExpiredNoticeMessage(message);

const findLastFlowAgentMessageIndex = (currentMessages: IMessage[]) => {
    for (let index = currentMessages.length - 1; index >= 0; index -= 1) {
        if (currentMessages[index].sender === 'agent' && !isSystemIdleMessage(currentMessages[index])) {
            return index;
        }
    }

    return -1;
};
const OPEN_DATA_CALCULATOR_ACTION = 'local_open_data_calculator';
const OPEN_EXTERNAL_URL_PREFIX = 'local_open_url::';
const DATA_CALCULATOR_URL = 'https://www.mtn.ng/datacalculator/?ppkdkd';
const BANNER_MESSAGE_ID_PREFIX = 'chat-banner-';
/** Set true to fetch and show the remote chat banner above the welcome message. */
const BANNER_ENABLED = false;
const MTN4ME_QUICK_ACTION = 'quick action: mtn4me';
const MTN4ME_QUICK_ACTION_LABEL = '📶 MTN4ME';
const DEFAULT_WELCOME_MESSAGE = "Let's get started 😀  \n\nType in anything you would like me to do for you today 👇🏾  <br /><br />For example: **I want to buy data**/**I want to check my balance**";

// Category labels used to group the welcome message's quick actions. The
// emoji prefix doubles as the button icon when rendered.
const MANAGE_DATA_CATEGORY = '↪️ Manage data';
const LIFESTYLE_SERVICES_CATEGORY = '✨ Lifestyle Services';
const DEFAULT_WELCOME_ACTIONS: NonNullable<IMessage['actions']> = [
    {
        label: 'How much data do I have?',
        type: 'quick action: How much data do I have?',
        action: 'quick action: How much data do I have?',
        category: MANAGE_DATA_CATEGORY
    },
    {
        label: 'My data finishes too fast',
        type: 'quick action: My data finishes too fast',
        action: 'quick action: My data finishes too fast',
        category: MANAGE_DATA_CATEGORY
    },
    {
        label: 'Teach me how to save data',
        type: 'quick action: Teach me how to save data',
        action: 'quick action: Teach me how to save data',
        category: MANAGE_DATA_CATEGORY
    },
    {
        label: '🌐 Calculate my data usage',
        type: 'button',
        action: OPEN_DATA_CALCULATOR_ACTION
    },
    {
        label: 'What subscriptions do I have?',
        type: 'quick action: What subscriptions do I have?',
        action: 'quick action: What subscriptions do I have?',
        category: LIFESTYLE_SERVICES_CATEGORY
    },
    {
        label: 'How vas subscriptions work',
        type: 'quick action: How subscriptions work',
        action: 'quick action: How subscriptions work',
        category: LIFESTYLE_SERVICES_CATEGORY
    },
    {
        label: 'How to avoid future vas charges',
        type: 'quick action: How to avoid future charges',
        action: 'quick action: How to avoid future charges',
        category: LIFESTYLE_SERVICES_CATEGORY
    },
    // {
    //     label: '📞 XtraTime/XtraByte Service',
    //     type: 'submit',
    //     action: 'quick action: xtrabyte_xtratime_services'
    // },
    {
        label: MTN4ME_QUICK_ACTION_LABEL,
        type: 'submit',
        action: MTN4ME_QUICK_ACTION
    },
    {
        label: '📞 XtraTime/XtraByte Service',
        type: 'submit',
        action: 'quick action: xtrabyte_xtratime_services'
    },
    {
        label: "Can't browse",
        type: 'submit',
        action: "quick action:Can't browse"
    },
    {
        label: 'Data analyzer',
        type: 'submit',
        action: 'quick action:Data analyzer'
    },
    ...(process.env.REACT_APP_ENABLE_VIEW_BILL_DEMO === 'true' ? [{
        label: 'View my bill (demo)',
        type: 'button',
        action: VIEW_BILL_DEMO_ACTION
    }] : [])
];

/** Keep banner links external when the API omits a protocol. */
const toExternalUrl = (url: string): string => {
    const trimmed = url.trim();
    if (!trimmed) {
        return trimmed;
    }
    if (/^[a-zA-Z][a-zA-Z\d+.-]*:/.test(trimmed)) {
        return trimmed;
    }
    if (trimmed.startsWith('//')) {
        return `https:${trimmed}`;
    }
    return `https://${trimmed.replace(/^\/+/, '')}`;
};

const getBannerCta = (banner: { ctaText?: string; ctaLink?: string }) => {
    const ctaText = banner.ctaText?.trim();
    const ctaLink = banner.ctaLink?.trim();

    if (!ctaLink) {
        return null;
    }

    const externalCtaLink = toExternalUrl(ctaLink);

    return {
        imageTitle: ctaText,
        imageLink: externalCtaLink
    };
};

const openExternalUrl = (url: string) => {
    try {
        if (window.ReactNativeWebView) {
            window.ReactNativeWebView.postMessage(JSON.stringify({
                type: 'OPEN_URL',
                url
            }));
            return;
        }

        if (window.parent !== window) {
            notifyEmbedParent('external_url', { url });
        }

        window.open(url, '_blank', 'noopener,noreferrer');
    } catch {
        window.location.href = url;
    }
};

const formatDateDisplay = (value: unknown): string => {
    if (typeof value !== 'string') {
        return String(value ?? '');
    }

    const isoDateMatch = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value.trim());
    if (!isoDateMatch) {
        return value;
    }

    const year = Number(isoDateMatch[1]);
    const month = Number(isoDateMatch[2]) - 1;
    const day = Number(isoDateMatch[3]);
    const date = new Date(Date.UTC(year, month, day));

    return new Intl.DateTimeFormat('en-GB', {
        day: 'numeric',
        month: 'short',
        timeZone: 'UTC'
    }).format(date);
};

const formatNumberToTwoDecimals = (value: number) => {
    if (!Number.isFinite(value)) {
        return '';
    }

    const rounded = Math.round((value + Number.EPSILON) * 100) / 100;
    return Number.isInteger(rounded) ? String(rounded) : rounded.toFixed(2).replace(/\.00$/, '');
};

const formatMeasurementDisplay = (value: unknown): string => {
    if (value === null || value === undefined) {
        return '';
    }

    if (typeof value === 'number') {
        return formatNumberToTwoDecimals(value);
    }

    if (typeof value !== 'string') {
        return String(value);
    }

    const trimmed = value.trim();
    const numericMatch = /^-?\d+(?:\.\d+)?/.exec(trimmed);
    if (!numericMatch) {
        return trimmed;
    }

    const numericValue = Number(numericMatch[0]);
    if (!Number.isFinite(numericValue)) {
        return trimmed;
    }

    const remainder = trimmed.slice(numericMatch[0].length);
    if (remainder.length > 0 && !/^\s/.test(remainder) && !/^[A-Za-z/%]/.test(remainder)) {
        return trimmed;
    }

    const unit = remainder.trim();
    const normalizedAmount = formatNumberToTwoDecimals(numericValue);

    return unit ? `${normalizedAmount} ${unit}` : normalizedAmount;
};

const parseNumericValue = (value: unknown): number => {
    if (typeof value === 'number') {
        return value;
    }

    if (typeof value !== 'string') {
        return 0;
    }

    const parsed = Number.parseFloat(value.replace(/[^0-9.-]/g, ''));
    return Number.isFinite(parsed) ? parsed : 0;
};

type HighlightAwareRow = IStructuredTablePayloadRowObject & {
    highlightIndexes?: number | number[];
    highlightCellIndex?: number;
};

const normalizeHighlightCellIndexes = (row: IStructuredTablePayloadRow | undefined): number[] => {
    if (!row || Array.isArray(row)) {
        return [];
    }

    const highlightAwareRow = row as HighlightAwareRow;
    const highlightIndexes = highlightAwareRow.highlightCellIndexes ?? highlightAwareRow.highlightIndexes;

    if (Array.isArray(highlightIndexes)) {
        return highlightIndexes;
    }

    if (typeof highlightIndexes === 'number') {
        return [highlightIndexes];
    }

    if (typeof highlightAwareRow.highlightCellIndex === 'number') {
        return [highlightAwareRow.highlightCellIndex];
    }

    if (highlightAwareRow.dataUsed !== undefined) {
        return [1];
    }

    return [];
};

const normalizeSummary = (
    summaryLike: IStructuredTablePayloadSummary | undefined
): IStructuredTableSection['summary'] | undefined => {
    if (!summaryLike) {
        return undefined;
    }

    if (typeof summaryLike === 'string') {
        return {
            label: summaryLike,
            value: ''
        };
    }

    return {
        label: summaryLike.label || 'Total',
        value: formatMeasurementDisplay(summaryLike.value ?? summaryLike.amount ?? ''),
        suffix: summaryLike.suffix || summaryLike.description
    };
};

const getStructuredSectionsSignature = (message: IMessage) => JSON.stringify(message.payload?.structuredSections || []);

const getProgressItemList = (
    progressSection: IStructuredProgressPayload | undefined
): IStructuredProgressPayloadItem[] => {
    if (!progressSection) {
        return [];
    }

    if (Array.isArray(progressSection)) {
        return progressSection;
    }

    return progressSection.items || progressSection.applications || progressSection.bars || [];
};

const getProgressSectionTitle = (
    progressSection: IStructuredProgressPayload | undefined
): string | undefined => {
    if (!progressSection || Array.isArray(progressSection)) {
        return undefined;
    }

    return progressSection.title;
};

const getCellValueAndColor = (
    cell: IStructuredTablePayloadCell
): { value: string | number; color?: string } => {
    if (typeof cell === 'object' && cell !== null) {
        return {
            value: cell.value ?? '',
            color: cell.color
        };
    }

    return { value: cell ?? '' };
};

const normalizeStructuredTableSection = (
    tableSection: IStructuredTablePayload | undefined,
    fallbackTitle = ''
): IStructuredTableSection => ({
    type: 'table',
    title: tableSection?.title || fallbackTitle,
    headers: tableSection?.headers || tableSection?.columns || [],
    rows: (tableSection?.rows || tableSection?.body || []).map((row: IStructuredTablePayloadRow) => {
        const rawCells: IStructuredTablePayloadCell[] = Array.isArray(row)
            ? row
            : (row.cells
                || row.values
                || [row.date, row.dataUsed, row.balance].filter(
                    (cell): cell is string | number => cell !== undefined
                ));

        return {
            cells: rawCells.map((cell, cellIndex) => {
                const { value, color } = getCellValueAndColor(cell);
                const normalizedValue = cellIndex === 0
                    ? formatDateDisplay(value)
                    : formatMeasurementDisplay(value);

                return {
                    value: normalizedValue,
                    color
                };
            }),
            highlightCellIndexes: normalizeHighlightCellIndexes(row)
        };
    }),
    summary: normalizeSummary(tableSection?.summary || tableSection?.footer || tableSection?.total)
});

const normalizeStructuredProgressSection = (
    progressSection: IStructuredProgressPayload | undefined,
    fallbackTitle = ''
): IStructuredProgressSection => {
    const items = getProgressItemList(progressSection);

    return {
        type: 'progress',
        title: getProgressSectionTitle(progressSection) || fallbackTitle,
        items: items.map((item: IStructuredProgressPayloadItem) => {
            const rawValue = parseNumericValue(
                item.percent ?? item.progress ?? item.ratio ?? item.value ?? item.amount ?? item.displayAmount
            );
            const maxValue = items.reduce((max: number, current: IStructuredProgressPayloadItem) => {
                const currentValue = parseNumericValue(
                    current.percent ?? current.progress ?? current.ratio ?? current.value ?? current.amount ?? current.displayAmount
                );
                return Number.isFinite(currentValue) && currentValue > max ? currentValue : max;
            }, 0);

            return {
                label: item.label || item.name || item.appName || '',
                value: formatMeasurementDisplay(item.displayAmount ?? item.amount ?? item.value ?? ''),
                percent: Number.isFinite(rawValue) && rawValue > 0
                    ? rawValue > 100
                        ? maxValue > 0
                            ? Math.round((rawValue / maxValue) * 100)
                            : 100
                        : rawValue
                    : 0,
                color: item.color || item.barColor || item.fillColor || '#ff4747',
                valueColor: item.valueColor || item.textColor || item.color || item.barColor || item.fillColor || '#111111'
            };
        })
    };
};

const normalizeStructuredSection = (
    section: IStructuredSectionPayload | null | undefined
): IStructuredMessageSection | null => {
    if (!section) {
        return null;
    }

    const fallbackTitle = section.title || section.message || '';

    if (section.type === 'table') {
        const tablePayload = section.table || section.payload?.table || (section as IStructuredTablePayload);
        return normalizeStructuredTableSection(tablePayload, fallbackTitle);
    }

    if (section.type === 'progress') {
        const progressPayload = section.progress || section.payload?.progress || (section as unknown as IStructuredProgressPayload);
        return normalizeStructuredProgressSection(progressPayload, fallbackTitle);
    }

    return null;
};

const buildStructuredSections = (responseData: IStructuredCapableResponse | undefined): IStructuredMessageSection[] => {
    const payload = responseData?.payload || {};

    if (Array.isArray(payload.structuredSections) && payload.structuredSections.length > 0) {
        return payload.structuredSections
            .map(section => normalizeStructuredSection(section as IStructuredSectionPayload))
            .filter((section): section is IStructuredMessageSection => section !== null);
    }

    if (Array.isArray(payload.sections) && payload.sections.length > 0) {
        return payload.sections
            .map(normalizeStructuredSection)
            .filter((section): section is IStructuredMessageSection => section !== null);
    }

    const structuredSections: IStructuredMessageSection[] = [];
    const tableSection = payload.table || payload.dataTable || responseData?.table;
    const progressSection = payload.progress || payload.applicationProgress || responseData?.progress;
    const fallbackTitle = responseData?.message || '';

    if (tableSection) {
        structuredSections.push(normalizeStructuredTableSection(tableSection, fallbackTitle));
    }

    if (progressSection) {
        structuredSections.push(normalizeStructuredProgressSection(progressSection, fallbackTitle));
    }

    return structuredSections;
};

const createStructuredAgentMessage = (responseData: IStructuredCapableResponse | undefined): IMessage | null => {
    const structuredSections = buildStructuredSections(responseData);

    if (!structuredSections.length) {
        return null;
    }

    return {
        id: Date.now().toString(),
        sender: 'agent',
        type: 'structured',
        text: responseData?.message || '',
        options: Array.from(new Set([
            ...(responseData?.metadata?.quickReplies || [])
        ])),
        actions: responseData?.payload?.actions || [],
        payload: {
            structuredSections
        }
    };
};

const mapWsSender = (sender?: string): 'agent' | 'customer' =>
    sender === 'server' || sender === 'ai' || sender === 'agent' ? 'agent' : 'customer';

const isSuccessfulWsStatusCode = (statusCode?: number) =>
    statusCode === undefined || statusCode === 200 || statusCode === 201;

type ParsedWsMessage =
    | { kind: 'response'; responseData: IStructuredCapableResponse; sender: 'agent' | 'customer' }
    | { kind: 'text'; text: string; sender: 'agent' | 'customer' };

const parseWsMessageEvent = (data: any): ParsedWsMessage | null => {
    if (data?.status === 'success') {
        return {
            kind: 'response',
            responseData: data.data ? data.data : data,
            sender: 'agent',
        };
    }

    const nestedMessage = data?.message;
    if (nestedMessage && typeof nestedMessage === 'object' && !Array.isArray(nestedMessage)) {
        if (isSuccessfulWsStatusCode(nestedMessage.statusCode) && nestedMessage.data) {
            return {
                kind: 'response',
                responseData: nestedMessage.data,
                sender: mapWsSender(data.sender),
            };
        }
    }

    if (typeof data?.message === 'string') {
        return {
            kind: 'text',
            text: data.message,
            sender: mapWsSender(data.sender),
        };
    }

    return null;
};

/** Convert the API's banner response into the same message shape used by the renderer. */
const createBannerAgentMessage = (
    responseData: IStructuredCapableResponse,
    sender: 'agent' | 'customer' = 'agent'
): IMessage | null => {
    if (responseData.type !== 'banner') {
        return null;
    }

    const banner = responseData.payload?.banner;
    if (!banner) {
        return null;
    }

    const cta = getBannerCta(banner);

    return {
        id: `${BANNER_MESSAGE_ID_PREFIX}${Date.now()}`,
        sender,
        type: 'message',
        text: banner.title?.trim() || responseData.message || '',
        image: banner.imageUrl?.trim() || undefined,
        // Banner actions are owned by the local welcome experience. Ignore any
        // actions returned by the API.
        actions: DEFAULT_WELCOME_ACTIONS,
        ...(cta ? { imageTitle: cta.imageTitle, imageLink: cta.imageLink } : {})
    };
};

const createAgentMessageFromResponseData = (
    responseData: IStructuredCapableResponse,
    sender: 'agent' | 'customer' = 'agent'
): IMessage => {
    const bannerMessage = createBannerAgentMessage(responseData, sender);
    if (bannerMessage) {
        return bannerMessage;
    }

    const structuredMessage = createStructuredAgentMessage(responseData);
    if (structuredMessage) {
        return { ...structuredMessage, sender };
    }

    const payload = responseData.payload as (IStructuredCapableResponse['payload'] & {
        fields?: IMessage['fields'];
    }) | undefined;
    const msgText = responseData.message || '';

    return {
        id: Date.now().toString(),
        sender,
        type: (responseData.type || 'message') as IMessage['type'],
        text: msgText,
        actions: payload?.actions || [],
        fields: payload?.fields || [],
        options: Array.from(new Set([...(responseData.metadata?.quickReplies || [])])),
        payload: {
            actions: payload?.actions || [],
            grid: payload?.grid,
            document: payload?.document
        },
        formField: responseData.type === 'form' && payload?.fields?.[0]
            ? {
                name: payload.fields[0].name,
                placeholder: payload.fields[0].placeholder,
                type: payload.fields[0].type,
                label: payload.fields[0].label,
                required: true,
                actions: payload?.actions
            }
            : undefined,
    };
};

const isRecentDuplicateAgentMessage = (
    recentMessages: IMessage[],
    candidate: IMessage,
    structuredSignature: string
): boolean =>
    recentMessages.some(message => {
        if (message.sender !== candidate.sender) {
            return false;
        }

        if (structuredSignature && candidate.type === 'structured') {
            return getStructuredSectionsSignature(message) === structuredSignature;
        }

        return message.text === candidate.text;
    });


interface ChatWidgetBodyProps {
    onIdleSessionReset?: () => void;
}

const ChatWidgetBody: React.FC<ChatWidgetBodyProps> = ({ onIdleSessionReset }) => {
    const { trackUserAction, trackApiCall } = useTracing();
    const { currentLanguage } = useLanguage();

    useEffect(() => {
        // ChatWidgetBody is remounted during the in-app idle-session reset, so
        // do not use component cleanup for logout. Page lifecycle events cover
        // browser/WebView exits without logging out during that remount.
        const isHomepage = window.location.pathname === '/';
        if (!isHomepage && !isAppIntegrationRoute()) {
            return;
        }

        let hasLoggedOut = false;
        const handlePageExit = () => {
            if (hasLoggedOut) {
                return;
            }

            hasLoggedOut = true;
            authService.logoutOnPageExit();
            webSocketService.disconnect();
        };

        window.addEventListener('pagehide', handlePageExit);
        window.addEventListener('beforeunload', handlePageExit);

        return () => {
            window.removeEventListener('pagehide', handlePageExit);
            window.removeEventListener('beforeunload', handlePageExit);
        };
    }, []);

    const [isInitializing, setIsInitializing] = useState(true);
    const [agentActive, setAgentActive] = useState(false);
    const [hasPendingInactivityPrompt, setHasPendingInactivityPrompt] = useState(false);
    const [inactivityResetKey, setInactivityResetKey] = useState(0);
    const lastActivityMessageIdRef = useRef<string | null>(null);
    const peripheralActivityThrottleRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    const [channel, setChannel] = useState<string>("Web");
    const [userMetadata, setUserMetaData] = useState<UserMetadataInterface | undefined>()
    const introContentRef = useRef<{ bannerMessage: IMessage | null; welcomeMessage: string }>({
        bannerMessage: null,
        welcomeMessage: DEFAULT_WELCOME_MESSAGE
    });

    const createDefaultBannerMessage = (): IMessage => ({
        id: `${Date.now()}-mtn4me-promo`,
        sender: 'agent',
        type: 'message',
        // Temp local default: skip API banner and show packaged MTN4ME creative.
        image: imageLinks.welcomeBanner,
        imageTitle: MTN4ME_QUICK_ACTION_LABEL,
        imageAction: MTN4ME_QUICK_ACTION
    });

    const createBannerMessageFromApi = (data: IChatBannerData): IMessage | null => {
        const banner = data.payload?.banner;
        if (!banner) {
            return null;
        }

        const image = banner.imageUrl?.trim();
        const cta = getBannerCta(banner);

        if (!image && !banner.ctaText?.trim() && !banner.ctaLink?.trim() && !banner.title?.trim()) {
            return null;
        }

        return {
            id: `${BANNER_MESSAGE_ID_PREFIX}api`,
            sender: 'agent',
            type: 'message',
            ...(image ? { image } : {}),
            // Only attach the clickable image when a link is present.
            ...(cta ? {
                imageTitle: cta.imageTitle,
                imageLink: cta.imageLink
            } : {})
        };
    };

    const resolveIntroContent = async (): Promise<{ bannerMessage: IMessage | null; welcomeMessage: string }> => {
        if (!BANNER_ENABLED) {
            return {
                bannerMessage: createDefaultBannerMessage(),
                welcomeMessage: DEFAULT_WELCOME_MESSAGE
            };
        }

        try {
            const response = await trackApiCall(
                'fetch_chat_banner',
                () => chatService.get(apiRoutes.chatBanner),
                { channel }
            );

            const data = response?.data?.data as IChatBannerData | undefined;
            const bannerMessage = data ? createBannerMessageFromApi(data) : null;
            const bannerTitle = data?.payload?.banner?.title?.trim();
            return {
                bannerMessage,
                welcomeMessage: bannerTitle || DEFAULT_WELCOME_MESSAGE
            };
        } catch {
            // Intentionally silent: fall back to the local (possibly empty) banner.
        }

        return {
            bannerMessage: createDefaultBannerMessage(),
            welcomeMessage: DEFAULT_WELCOME_MESSAGE
        };
    };

    const createDefaultMessage = (firstName?: string, bannerMessage?: IMessage | null, welcomeMessage?: string): IMessage => {
        const resolvedBanner = bannerMessage ?? createDefaultBannerMessage();
        const resolvedWelcomeMessage = (welcomeMessage || DEFAULT_WELCOME_MESSAGE).trim();

        return {
            ...resolvedBanner,
            id: resolvedBanner.id || Date.now().toString(),
            sender: 'agent',
            type: 'message',
            text: `${firstName ? `**Y'ello! ${firstName}**\n\n` : ''}${resolvedWelcomeMessage}`,
            actions: [...DEFAULT_WELCOME_ACTIONS, ...(resolvedBanner.actions || [])]
        };
    };

    // Intro is optional; empty API title / empty local fallback means welcome only.
    const createInitialMessages = (firstName?: string, introContent?: { bannerMessage: IMessage | null; welcomeMessage: string }): IMessage[] => {
        const resolvedIntroContent = introContent !== undefined
            ? introContent
            : introContentRef.current;

        return [
            createDefaultMessage(firstName, resolvedIntroContent.bannerMessage, resolvedIntroContent.welcomeMessage)
        ];
    };

    const [messages, setMessages] = useState<IMessage[]>(() => createInitialMessages());


    const [isTyping, setIsTyping] = useState(false);
    const [recordingStartSignal, setRecordingStartSignal] = useState(0);
    const messagesEndRef = useRef<HTMLDivElement>(null);
    const inactivityTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const inactivityScheduleGenerationRef = useRef(0);
    const idleSessionResetTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const idleSessionResetGenerationRef = useRef(0);
    // Mirror of `messages` so the inactivity effect can read the latest value
    // without needing to depend on the array (which would cause it to re-run
    // on every message and pollute the dep list).
    const messagesRef = useRef<IMessage[]>([]);
    messagesRef.current = messages;
    const [conversationId, setConversationId] = useState<string | null>(() => {
        return sessionStorage.getItem('conversationId');
    });
    const conversationIdRef = useRef<string | null>(conversationId);
    conversationIdRef.current = conversationId;

    const { sendMessage: sendWebSocketMessage, subscribe, isConnected } = useWebSocket(conversationId || undefined);

    const scrollToBottom = () => {
        setTimeout(() => {
            const chatWrapper = document.querySelector('.chat__widget__body--wrapper');
            if (chatWrapper) {
                chatWrapper.scrollTop = chatWrapper.scrollHeight;
            }
        }, 100);
    };

    const createInactivityPromptMessage = (): IMessage => ({
        id: `${INACTIVITY_REMINDER_ID_PREFIX}${Date.now()}`,
        sender: 'agent',
        type: 'plain',
        text: getInactivityReminderText(),
        actions: [
            {
                label: 'Continue',
                type: 'button',
                action: INACTIVITY_CONTINUE_ACTION
            },
            {
                label: 'Cancel',
                type: 'button',
                action: INACTIVITY_CANCEL_ACTION
            }
        ]
    });

    const createSessionExpiredNoticeMessage = (): IMessage => ({
        id: `${SESSION_EXPIRED_NOTICE_ID_PREFIX}${Date.now()}`,
        sender: 'agent',
        type: 'plain',
        text: SESSION_EXPIRED_RESTART_TEXT
    });

    const clearLastAgentControls = useCallback((currentMessages: IMessage[]) => {
        const actualIndex = findLastFlowAgentMessageIndex(currentMessages);
        if (actualIndex === -1) {
            return currentMessages;
        }

        return currentMessages.map((message, index) =>
            index === actualIndex ? {
                ...message,
                options: [],
                actions: [],
                formField: message.formField ? { ...message.formField, name: '' } : undefined
            } : message
        );
    }, []);

    const clearInactivityTimer = useCallback(() => {
        if (inactivityTimeoutRef.current) {
            clearTimeout(inactivityTimeoutRef.current);
            inactivityTimeoutRef.current = null;
        }
    }, []);

    const clearIdleSessionResetTimer = useCallback(() => {
        if (idleSessionResetTimeoutRef.current) {
            clearTimeout(idleSessionResetTimeoutRef.current);
            idleSessionResetTimeoutRef.current = null;
        }
    }, []);

    const clearPeripheralActivityThrottle = useCallback(() => {
        if (peripheralActivityThrottleRef.current) {
            clearTimeout(peripheralActivityThrottleRef.current);
            peripheralActivityThrottleRef.current = null;
        }
    }, []);

    const invalidateInactivitySchedule = useCallback(() => {
        inactivityScheduleGenerationRef.current += 1;
        clearInactivityTimer();
        clearPeripheralActivityThrottle();
    }, [clearInactivityTimer, clearPeripheralActivityThrottle]);

    /**
     * Mint / resume a conversation only when the customer actually sends a message.
     * Opening chat, welcome CTA, and idle remount must not create a backend session.
     */
    const ensureConversationSession = useCallback((options?: { forceNew?: boolean }): string => {
        const existingId = conversationIdRef.current || sessionStorage.getItem('conversationId');
        const activeId = options?.forceNew || !existingId ? createUUID() : existingId;

        sessionStorage.setItem('conversationId', activeId);
        conversationIdRef.current = activeId;
        setConversationId(activeId);

        if (options?.forceNew) {
            setAgentActive(false);
            setHasPendingInactivityPrompt(false);
            setInactivityResetKey(key => key + 1);
        }

        webSocketService.connect({ conversationId: activeId });
        webSocketService.joinConversation(activeId);
        return activeId;
    }, []);

    const handleIdleSessionReset = useCallback((options?: { dueToInactivityTimeout?: boolean }) => {
        invalidateInactivitySchedule();
        idleSessionResetGenerationRef.current += 1;
        clearIdleSessionResetTimer();
        webSocketService.disconnect();
        sessionStorage.removeItem('conversationId');
        conversationIdRef.current = null;
        setConversationId(null);
        setHasPendingInactivityPrompt(false);

        if (options?.dueToInactivityTimeout) {
            markChatSessionExpired();
        }

        // Clear transactional journey controls so the user must restart from the beginning.
        setMessages(prevMessages => {
            const withoutReminder = prevMessages.filter(message => !isInactivityPromptMessage(message));
            const clearedControls = clearLastAgentControls(withoutReminder);
            const lastFlowIndex = findLastFlowAgentMessageIndex(clearedControls);

            if (lastFlowIndex === -1) {
                return clearedControls;
            }

            return clearedControls.map((message, index) => {
                if (index !== lastFlowIndex || message.type !== 'form') {
                    return message;
                }

                return {
                    ...message,
                    type: 'plain',
                    formField: undefined,
                    fields: undefined,
                    payload: message.payload
                        ? { ...message.payload, formFields: undefined, actions: undefined }
                        : undefined
                };
            });
        });

        onIdleSessionReset?.();
    }, [clearIdleSessionResetTimer, clearLastAgentControls, invalidateInactivitySchedule, onIdleSessionReset]);

    const notifyPeripheralActivity = useCallback(() => {
        if (hasPendingInactivityPrompt) {
            return;
        }

        if (peripheralActivityThrottleRef.current) {
            return;
        }

        const generation = inactivityScheduleGenerationRef.current;
        peripheralActivityThrottleRef.current = setTimeout(() => {
            peripheralActivityThrottleRef.current = null;
            if (generation !== inactivityScheduleGenerationRef.current) {
                return;
            }

            setInactivityResetKey(key => key + 1);
        }, 400);
    }, [hasPendingInactivityPrompt]);

    const handleVoiceRecordingStart = useCallback(() => {
        setRecordingStartSignal(signal => signal + 1);
    }, []);

    const handleChatMouseMove = useCallback(() => {
        if (isInitializing || hasPendingInactivityPrompt) {
            return;
        }

        notifyPeripheralActivity();
    }, [hasPendingInactivityPrompt, isInitializing, notifyPeripheralActivity]);

    const handleLocalInactivityAction = useCallback((currentMessages: IMessage[], selectedLabel: string, selectedAction?: string) => {
        if (selectedAction === INACTIVITY_CONTINUE_ACTION) {
            setInactivityResetKey(key => key + 1);
            return currentMessages.filter(message => !isInactivityPromptMessage(message));
        }

        if (selectedAction === INACTIVITY_CANCEL_ACTION) {
            handleIdleSessionReset();
            return currentMessages.filter(message => !isInactivityPromptMessage(message));
        }

        return currentMessages;
    }, [handleIdleSessionReset]);

    // Reusable function to request newer messages with latest timestamp
    const requestNewerMessages = useCallback(() => {
        if (!conversationId) {
            console.log('[WS] requestNewerMessages skipped: no conversationId');
            return;
        }

        // Get the latest timestamp from messages
        // Since message IDs are created with Date.now().toString(), we can use the latest ID as timestamp
        let latestTimestamp: number | undefined;
        
        if (messages.length > 0) {
            // Use the latest message ID as timestamp (message IDs are created with Date.now().toString())
            const latestMessage = messages[messages.length - 1];
            const messageIdAsNumber = parseInt(latestMessage.id, 10);
            if (!isNaN(messageIdAsNumber) && messageIdAsNumber > 0) {
                latestTimestamp = messageIdAsNumber;
            }
        }

        console.log('[WS] requestNewerMessages:', { conversationId, latestTimestamp });
        webSocketService.requestNewerMessages(conversationId);
        
    }, [conversationId, messages]);

    useEffect(() => {
        if (isInitializing) {
            return;
        }

        const lastMessage = messages[messages.length - 1];
        if (!lastMessage || isInactivityPromptMessage(lastMessage)) {
            return;
        }

        if (lastActivityMessageIdRef.current === lastMessage.id) {
            return;
        }

        lastActivityMessageIdRef.current = lastMessage.id;
        setInactivityResetKey(key => key + 1);
    }, [messages, isInitializing]);

    useEffect(() => {
        return () => {
            invalidateInactivitySchedule();
            idleSessionResetGenerationRef.current += 1;
            clearIdleSessionResetTimer();
        };
    }, [clearIdleSessionResetTimer, invalidateInactivitySchedule]);

    useEffect(() => {
        if (isInitializing || hasPendingInactivityPrompt) {
            invalidateInactivitySchedule();
            return;
        }

        const generation = inactivityScheduleGenerationRef.current + 1;
        inactivityScheduleGenerationRef.current = generation;

        inactivityTimeoutRef.current = setTimeout(() => {
            if (generation !== inactivityScheduleGenerationRef.current) {
                return;
            }

            // Manual check via ref so we don't have to depend on `messages`.
            // Skip nudging the user if they haven't sent anything yet — i.e.
            // the chat is still showing only the default welcome message.
            const hasCustomerActivity = messagesRef.current.some(
                message => message.sender === 'customer'
            );
            if (!hasCustomerActivity) {
                return;
            }

            setMessages(prevMessages => {
                const hasExistingPrompt = prevMessages.some(isInactivityPromptMessage);

                if (hasExistingPrompt) {
                    return prevMessages;
                }

                return [...prevMessages, createInactivityPromptMessage()];
            });
            setHasPendingInactivityPrompt(true);
            scrollToBottom();
        }, INACTIVITY_REMINDER_MS);

        return () => {
            invalidateInactivitySchedule();
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [invalidateInactivitySchedule, hasPendingInactivityPrompt, inactivityResetKey, isInitializing]);

    // Hard session reset after configured idle timeout. Continues even while the
    // inactivity reminder is on screen, so ignoring that prompt still
    // ends the session and requires a restart (including transactional journeys).
    useEffect(() => {
        clearIdleSessionResetTimer();

        if (isInitializing) {
            return;
        }

        const generation = idleSessionResetGenerationRef.current + 1;
        idleSessionResetGenerationRef.current = generation;

        idleSessionResetTimeoutRef.current = setTimeout(() => {
            if (generation !== idleSessionResetGenerationRef.current) {
                return;
            }

            const hasCustomerActivity = messagesRef.current.some(
                message => message.sender === 'customer'
            );
            if (!hasCustomerActivity) {
                return;
            }

            handleIdleSessionReset({ dueToInactivityTimeout: true });
        }, SESSION_TIMEOUT_MS);

        return () => {
            clearIdleSessionResetTimer();
        };
    }, [
        clearIdleSessionResetTimer,
        handleIdleSessionReset,
        inactivityResetKey,
        isInitializing
    ]);

    useEffect(() => {
        console.log('[WS] registering "message" subscription');
        const unsubscribe = subscribe('message', (data: any) => {
            console.log('[WS][recv] "message" event payload:', data);
            setIsTyping(false);

            const parsed = parseWsMessageEvent(data);
            if (!parsed) {
                return;
            }

            if (parsed.kind === 'response') {
                const intentSubject = (parsed.responseData.metadata as { intent?: { subject?: string } } | undefined)
                    ?.intent?.subject;
                if (intentSubject === 'transfer_to_agent') {
                    setAgentActive(true);
                }

                const agentMessage = createAgentMessageFromResponseData(parsed.responseData, parsed.sender);
                const structuredSignature = agentMessage.type === 'structured'
                    ? getStructuredSectionsSignature(agentMessage)
                    : '';

                setMessages(prev => {
                    const recentMessages = prev.slice(-5);
                    if (isRecentDuplicateAgentMessage(recentMessages, agentMessage, structuredSignature)) {
                        console.log('[WS][message] Skipping duplicate message:', agentMessage.text);
                        return prev;
                    }

                    return [...prev, agentMessage];
                });
                scrollToBottom();
                return;
            }

            const msg: IMessage = {
                id: Date.now().toString(),
                sender: parsed.sender,
                type: 'message',
                text: parsed.text,
            };

            setMessages(prev => {
                const recentMessages = prev.slice(-5);
                if (isRecentDuplicateAgentMessage(recentMessages, msg, '')) {
                    console.log('[WS][message] Skipping duplicate message:', parsed.text);
                    return prev;
                }

                return [...prev, msg];
            });
            scrollToBottom();
        });

        return () => {
            console.log('[WS] unsubscribing "message"');
            unsubscribe();
        };
    }, [subscribe]);


    useEffect(() => {
        console.log('[WS] registering "agent_general" subscription');
        const unsubscribe = subscribe('agent_general', (data: any) => {
            console.log('[WS][recv] "agent_general" event payload:', data);
        });

        return () => {
            console.log('[WS] unsubscribing "agent_general"');
            unsubscribe();
        };
    }, [subscribe]);

    // Listen for new-messages event
    useEffect(() => {
        console.log('[WS] registering "new-messages" subscription');
        const unsubscribe = subscribe('new-messages', (data: any) => {
            console.log('[WS][recv] "new-messages" event payload:', data);
            
            if (Array.isArray(data)) {
                // Handle array of messages
                const newMessages: IMessage[] = data.map((msg: any) => {
                    const structuredMessage = createStructuredAgentMessage(msg);
                    if (structuredMessage) {
                        return {
                            ...structuredMessage,
                            id: msg.id || createUUID(),
                            sender: msg.sender === 'ai' || msg.sender === 'agent' ? 'agent' : 'customer'
                        };
                    }

                    return {
                        id: msg.id || createUUID(),
                        sender: msg.sender === 'ai' || msg.sender === 'agent' ? 'agent' : 'customer',
                        type: msg.type || 'message',
                        text: msg.message || msg.text || '',
                        options: msg.metadata?.quickReplies || [],
                        actions: msg.payload?.actions || [],
                        fields: msg.payload?.fields || [],
                        payload: {
                            actions: msg.payload?.actions || [],
                            grid: msg.payload?.grid,
                            document: msg.payload?.document
                        },
                        formField: msg.type === 'form' && msg.payload?.fields?.length > 0 && msg.payload.fields[0]
                            ? {
                                name: msg.payload.fields[0].name,
                                placeholder: msg.payload.fields[0].placeholder,
                                type: msg.payload.fields[0].type,
                                label: msg.payload.fields[0].label,
                                required: true,
                                actions: msg.payload.actions
                            }
                            : undefined
                    };
                });

                // Add new messages to the state, avoiding duplicates
                setMessages(prev => {
                    const existingIds = new Set(prev.map(m => m.id));
                    const uniqueNewMessages = newMessages.filter(msg => !existingIds.has(msg.id));
                    if (uniqueNewMessages.length > 0) {
                        return [...prev, ...uniqueNewMessages];
                    }
                    return prev;
                });
                scrollToBottom();
            } else if (data?.messages && Array.isArray(data.messages)) {
                // Handle object with messages array
                const newMessages: IMessage[] = data.messages.map((msg: any) => {
                    const structuredMessage = createStructuredAgentMessage(msg);
                    if (structuredMessage) {
                        return {
                            ...structuredMessage,
                            id: msg.id || createUUID(),
                            sender: msg.sender === 'ai' || msg.sender === 'agent' ? 'agent' : 'customer'
                        };
                    }

                    return {
                        id: msg.id || createUUID(),
                        sender: msg.sender === 'ai' || msg.sender === 'agent' ? 'agent' : 'customer',
                        type: msg.type || 'message',
                        text: msg.message || msg.text || '',
                        options: msg.metadata?.quickReplies || [],
                        actions: msg.payload?.actions || [],
                        fields: msg.payload?.fields || [],
                        payload: {
                            actions: msg.payload?.actions || [],
                            grid: msg.payload?.grid,
                            document: msg.payload?.document
                        },
                        formField: msg.type === 'form' && msg.payload?.fields?.length > 0 && msg.payload.fields[0]
                            ? {
                                name: msg.payload.fields[0].name,
                                placeholder: msg.payload.fields[0].placeholder,
                                type: msg.payload.fields[0].type,
                                label: msg.payload.fields[0].label,
                                required: true,
                                actions: msg.payload.actions
                            }
                            : undefined
                    };
                });

                setMessages(prev => {
                    const existingIds = new Set(prev.map(m => m.id));
                    const uniqueNewMessages = newMessages.filter(msg => !existingIds.has(msg.id));
                    if (uniqueNewMessages.length > 0) {
                        return [...prev, ...uniqueNewMessages];
                    }
                    return prev;
                });
                scrollToBottom();
            }
        });

        return () => {
            console.log('[WS] unsubscribing "new-messages"');
            unsubscribe();
        };
    }, [subscribe]);

    const handleSendVoiceMessage = async (message: {
        content: Blob;
        duration: number;
    }) => {
        setHasPendingInactivityPrompt(false);

        const restartingExpiredSession = consumeChatSessionExpiredNotice();
        const activeConversationId = ensureConversationSession({
            forceNew: restartingExpiredSession
        });

        // Create a voice message with initial uploading state
        const messageId = Date.now().toString();
        const voiceMessage: IMessage = {
            id: messageId,
            sender: 'customer',
            type: 'audio',
            text: '',
            payload: {
                audioSource: message.content,
                uploadState: 'uploading', // Add upload state
                duration: message.duration // Store duration from recording
            }
        };

        setMessages(prev => {
            if (restartingExpiredSession) {
                return [
                    ...createInitialMessages(userMetadata?.firstName),
                    createSessionExpiredNoticeMessage(),
                    voiceMessage
                ];
            }
            return [...prev, voiceMessage];
        });
        setIsTyping(true);
        scrollToBottom();

        // Create FormData for file upload
        const formData = new FormData();

        // Create a file from the blob with a proper filename and type (16 kHz mono WAV)
        const audioMimeType = message.content.type || 'audio/wav';
        const audioExtension = audioMimeType.includes('wav')
            ? 'wav'
            : audioMimeType.includes('webm')
                ? 'webm'
                : audioMimeType.includes('ogg')
                    ? 'ogg'
                    : audioMimeType.includes('mp4') || audioMimeType.includes('m4a')
                        ? 'm4a'
                        : 'wav';
        const audioFile = new File(
            [message.content],
            `voice-message-${messageId}.${audioExtension}`,
            { type: audioMimeType }
        );

        const finalChannel = (channel === "MyMTNAPP" ? 'app' : channel).toLowerCase();
        formData.append('file', audioFile);
        formData.append('channel', finalChannel);
        if (currentLanguage?.code) {
            formData.append('language', currentLanguage?.code);
        }
        const chatSource = getChatSource();
        if (chatSource) {
            formData.append('source', chatSource);
        }

        try {

            // Track API call
            const apiCall = () => chatService.post(apiRoutes.chatUpload, formData, {
                headers: {
                    'Content-Type': 'multipart/form-data',
                    'X-Language': currentLanguage?.code,
                    'X-Channel': finalChannel
                }
            });

            const response = await trackApiCall('upload_voice_message', apiCall, {
                channel: finalChannel,
                messageType: 'audio',
                conversationId: activeConversationId
            });


            if (response.status === 200 || response.status === 201) {
                // Update message with successful upload state and server URL if provided

                setMessages(prev => prev.map(msg =>
                    msg.id === messageId ? {
                        ...msg,
                        payload: {
                            ...msg.payload,
                            uploadState: 'success'
                        }
                    } : msg
                ));


                // Handle agent response based on the new API response format

                const responseData = response.data.data;
                // Create agent message from the response
                let agentMessages: IMessage[] = [];
                const structuredMessage = createStructuredAgentMessage(responseData);
                if (structuredMessage) {
                    agentMessages.push(structuredMessage);
                } else if (responseData.type === "group") {
                    agentMessages = responseData.payload.messages.map(
                        (mssg: GroupMessagesPayload, index: number) => ({
                            id: createUUID(),
                            sender: 'agent',
                            type: mssg.type,
                            text: mssg.message,
                            actions: index === responseData.payload.messages.length - 1 ? responseData.payload.actions
                                : undefined,
                            options: Array.from(new Set([
                                ...(mssg.metadata?.quickReplies || [])
                            ])),
                            payload: {
                                actions: mssg.payload?.actions || [],
                                grid: mssg.payload?.grid,
                                document: mssg.payload?.document
                            },
                            formField: mssg.type === 'form' && mssg.payload?.fields && mssg.payload?.fields?.length > 0 && mssg.payload.fields[0]
                                ? {
                                    name: mssg.payload.fields[0].name,
                                    placeholder: mssg.payload.fields[0].placeholder,
                                    type: mssg.payload.fields[0].type,
                                    label: mssg.payload.fields[0].label,
                                    required: true,
                                    actions: mssg.payload.actions
                                }
                                : undefined
                        })
                    );
                } else {
                    agentMessages.push({
                        id: Date.now().toString(),
                        sender: 'agent',
                        type: responseData.type || 'message',
                        text: responseData.message || '',
                        options: Array.from(new Set([
                            ...(responseData.metadata?.quickReplies || []),
                        ])),
                        actions: responseData.payload?.actions || [],
                        payload: {
                            actions: responseData.payload?.actions || [],
                            grid: responseData.payload?.grid,
                            document: responseData.payload?.document
                        },
                        formField: responseData.type === 'form' && responseData.payload?.fields?.length > 0 && responseData.payload.fields[0]
                            ? {
                                name: responseData.payload.fields[0].name,
                                placeholder: responseData.payload.fields[0].placeholder,
                                type: responseData.payload.fields[0].type,
                                label: responseData.payload.fields[0].label,
                                required: true,
                                actions: responseData.payload.actions
                            }
                            : undefined

                    })
                }

                   // Check for audio URL or audio_data in metadata and create voice note message
                   let audioSource: string | undefined;

                   // First, check for URL (preferred method)
                   if (responseData?.metadata?.media?.url &&
                    typeof responseData.metadata.media.url === 'string' &&
                    responseData.metadata.media.url.trim().length > 0) {
                    const audioUrl = responseData.metadata.media.url.trim();
                    // Validate that it's a valid URL
                    try {
                        new URL(audioUrl);
                        audioSource = audioUrl;
                    } catch (e) {
                        // Invalid URL, skip
                    }
                   }
                   
                   // Fallback to audio_data (base64 data URI)
                   if (!audioSource && 
                    responseData?.metadata?.media?.audio_data &&
                    typeof responseData.metadata.media.audio_data === 'string' &&
                    responseData.metadata.media.audio_data.trim().length > 0) {

                    const audioData = responseData.metadata.media.audio_data.trim();

                    // Validate that it's a base64 audio data URI (starts with "data:audio/")
                    if (audioData.startsWith('data:audio/')) {
                        audioSource = audioData;
                    }
                }

                   // Create voice note message if we have a valid audio source
                   if (audioSource) {
                        const voiceNoteMessage: IMessage = {
                            id: Date.now().toString() + '-audio',
                            sender: 'agent',
                            type: 'audio',
                            text: '',
                            payload: {
                                audioSource: audioSource,
                                uploadState: 'success'
                            }
                        };
                        agentMessages.push(voiceNoteMessage);
                   }

                setMessages((prevMessages) => ([...prevMessages, ...agentMessages]));

                // Store conversation ID if provided
                if (responseData.metadata?.conversationId) {
                    const backendConversationId = responseData.metadata.conversationId;
                    conversationIdRef.current = backendConversationId;
                    setConversationId(backendConversationId);
                    sessionStorage.setItem('conversationId', backendConversationId);
                }

            }
        } catch (error: any) {
            // console.error('Error uploading voice message:', error);

            // Update message with failed upload state
            setMessages(prev => prev.map(msg =>
                msg.id === messageId ? {
                    ...msg,
                    payload: {
                        ...msg.payload,
                        uploadState: 'failed',
                        errorMessage: error.message
                    }
                } : msg
            ));

            trackUserAction('voice_message_upload_error', {
                error: error.message,
                channel
            });
        } finally {
            setIsTyping(false);
            scrollToBottom();
        }
    };

    const handleRetryVoiceUpload = (messageId: string) => {
        // Find the message
        const messageToRetry = messages.find(msg => msg.id === messageId);
        if (!messageToRetry || !messageToRetry.payload?.audioSource || !(messageToRetry.payload.audioSource instanceof Blob)) {
            return;
        }

        // Get the original blob and duration
        const originalBlob = messageToRetry.payload.audioSource as Blob;
        const originalDuration = messageToRetry.payload.duration || 0;

        // Call handleSendVoiceMessage with the original content and duration
        handleSendVoiceMessage({
            content: originalBlob,
            duration: originalDuration
        });

        // Remove the failed message
        setMessages(prev => prev.filter(msg => msg.id !== messageId));
    };

    const handleViewBillDemoGridSubmit = (label: string) => {
        setMessages(currentMessages => ([
            ...clearLastAgentControls(currentMessages),
            {
                id: `${VIEW_BILL_DEMO_GRID_ID_PREFIX}customer-${Date.now()}`,
                sender: 'customer',
                type: 'message',
                text: label
            },
            ...createViewBillDemoDocumentMessages(label)
        ]));
        scrollToBottom();
    };

    const handleSendMessage = async (text: string, action?: string, type?: string) => {
        try {
            if (!text.trim()) return;
            setHasPendingInactivityPrompt(false);

            console.log('[SEND] user input:', { text, isConnected, agentActive, action, type, channel, conversationId });

            trackUserAction('send_message', {
                messageType: action ? 'action' : 'text',
                action: action || 'text_input',
                channel
            });

            const updatedMessages = clearLastAgentControls(messages);

            if (
                action === INACTIVITY_CONTINUE_ACTION ||
                action === INACTIVITY_CANCEL_ACTION
            ) {
                setMessages(handleLocalInactivityAction(messages, text, action));
                setIsTyping(false);
                scrollToBottom();
                return;
            }

            if (action === OPEN_DATA_CALCULATOR_ACTION) {
                trackUserAction('open_external_link', {
                    url: DATA_CALCULATOR_URL,
                    source: 'quick_action',
                    channel
                });
                openExternalUrl(DATA_CALCULATOR_URL);
                setIsTyping(false);
                return;
            }

            if (action === VIEW_BILL_DEMO_ACTION) {
                setMessages(currentMessages => ([
                    ...clearLastAgentControls(currentMessages),
                    createViewBillDemoGridMessage()
                ]));
                setIsTyping(false);
                scrollToBottom();
                return;
            }

            if (action === VIEW_BILL_DEMO_SHARE_ACTION) {
                setMessages(currentMessages => ([
                    ...clearLastAgentControls(currentMessages),
                    createViewBillDemoShareMessage()
                ]));
                setIsTyping(false);
                scrollToBottom();
                return;
            }

            if (action?.startsWith(OPEN_EXTERNAL_URL_PREFIX)) {
                const externalUrl = action.slice(OPEN_EXTERNAL_URL_PREFIX.length).trim();
                if (externalUrl) {
                    trackUserAction('open_external_link', {
                        url: externalUrl,
                        source: 'banner_cta',
                        channel
                    });
                    openExternalUrl(externalUrl);
                }
                setIsTyping(false);
                return;
            }

            // Real outbound message — create/resume the session only now.
            const restartingExpiredSession = consumeChatSessionExpiredNotice();
            const activeConversationId = ensureConversationSession({
                forceNew: restartingExpiredSession
            });

            // Handle end_agent_chat action explicitly
            const isEndAgentChat = action === 'end_agent_chat';
            if (isEndAgentChat) {
                setAgentActive(false);
            }

            const userMessage: IMessage = {
                id: Date.now().toString(),
                sender: 'customer',
                type: 'message',
                text
            };

            setMessages(() => {
                if (restartingExpiredSession) {
                    // Wipe prior chat history, keep the default welcome + restart notice.
                    return [
                        ...createInitialMessages(userMetadata?.firstName),
                        createSessionExpiredNoticeMessage(),
                        userMessage
                    ];
                }
                return [...updatedMessages, userMessage];
            });

            setIsTyping(true);
            scrollToBottom();

            const url = channel === "Web" ? apiRoutes.chat : apiRoutes.chatApp;
            const chatSource = getChatSource();
            // Free-text chat input has no action; buttons / quick actions / form submits pass one.
            const inputType = action ? 'button' : 'input';
            const requestPayload = {
                message: type === "form-submit"
                    ? text
                    : (type === "submit")
                        ? action
                        : (action === "next" || !action)
                            ? text
                            : action,
                metadata: {
                    firstName: userMetadata?.firstName,
                    phone: userMetadata?.phone,
                    language: currentLanguage?.code,
                    inputType,
                    ...(chatSource ? { source: chatSource } : {})
                }
            };

            // Prefer WebSocket for real-time delivery; fallback to HTTP if disconnected
            // Note: For end_agent_chat, we still allow WebSocket even if agentActive is false (just set above)
            if (isConnected && (agentActive || isEndAgentChat)) {
                console.log('[WS][send] via WebSocket:', { message: requestPayload.message, metadata: requestPayload.metadata, agentActive, isConnected, isEndAgentChat });
                // Align payload to { message: ... }
                sendWebSocketMessage({ message: requestPayload.message });
                // Request newer messages after sending
                requestNewerMessages();
                // Return early - let WebSocket handle the response to prevent duplicate messages
                return;
            }

            console.log('[HTTP][send] via REST:', { url, payload: requestPayload, agentActive, isConnected });
            const apiCall = () => chatService.post(url, requestPayload);

            const response = await trackApiCall('send_chat_message', apiCall, {
                channel,
                messageType: action ? 'action' : 'text',
                conversationId: activeConversationId
            });

            console.log('[HTTP][recv] response:', { status: response.status, data: response.data });

            if (response.status === 200 || response.status === 201) {
                const data = response.data.data;
                const intentSubject = data?.metadata?.intent?.subject;
                const activateAgent = intentSubject === 'transfer_to_agent';
                console.log('[AGENT] intent subject:', intentSubject, 'activateAgent:', activateAgent);
                
                // Don't reactivate agent if we just ended the chat
                if (!isEndAgentChat) {
                    setAgentActive(activateAgent);
                }

                if (data?.metadata?.conversationId) {
                    const backendConversationId = data.metadata.conversationId as string;
                    conversationIdRef.current = backendConversationId;
                    setConversationId(backendConversationId);
                    sessionStorage.setItem('conversationId', backendConversationId);
                }

                // Manually reconnect socket and join room when agent is activated
                if (activateAgent) {
                    const roomConversationId = conversationIdRef.current || activeConversationId;
                    if (roomConversationId) {
                        const roomName = `room_${roomConversationId}`;
                        console.log('[WS][manual] reconnecting and joining:', roomName);
                        try {
                            webSocketService.disconnect();
                            webSocketService.connect({ conversationId: roomConversationId });
                            webSocketService.joinConversation(roomConversationId);
                            console.log('[WS][manual] reconnect complete; join emitted for:', roomName);
                        } catch (err) {
                            console.error('[WS][manual] reconnect/join failed:', err);
                        }
                    } else {
                        console.warn('[WS][manual] missing conversationId; cannot join room');
                    }
                }

                let agentMessages: IMessage[] = [];
                const bannerMessage = createBannerAgentMessage(data);
                const structuredMessage = createStructuredAgentMessage(data);
                if (bannerMessage) {
                    agentMessages.push(bannerMessage);
                } else if (structuredMessage) {
                    agentMessages.push(structuredMessage);
                } else if (data.type === "group") {
                    const groupMessages = data.payload.messages as GroupMessagesPayload[];
                    const lastIndex = groupMessages.length - 1;

                    agentMessages = groupMessages.map((mssg: GroupMessagesPayload, index: number): IMessage => {
                        const isLast = index === lastIndex;
                        const trailingActions = isLast ? data.payload.actions : undefined;
                        const trailingFields = isLast ? data.payload.fields : undefined;

                        const structuredGroupMessage = createStructuredAgentMessage(mssg);
                        if (structuredGroupMessage) {
                            return {
                                ...structuredGroupMessage,
                                id: createUUID(),
                                actions: trailingActions || structuredGroupMessage.actions,
                                fields: trailingFields ?? structuredGroupMessage.fields
                            };
                        }

                        return {
                            id: createUUID(),
                            sender: 'agent',
                            type: mssg.type,
                            text: mssg.message,
                            actions: trailingActions,
                            fields: trailingFields,
                            options: Array.from(new Set([
                                ...(mssg.metadata?.quickReplies || [])
                            ])),
                            payload: {
                                actions: mssg.payload?.actions || [],
                                grid: mssg.payload?.grid,
                                document: mssg.payload?.document
                            },
                            formField: mssg.type === 'form' && mssg.payload?.fields && mssg.payload?.fields?.length > 0 && mssg.payload.fields[0]
                                ? {
                                    name: mssg.payload.fields[0].name,
                                    placeholder: mssg.payload.fields[0].placeholder,
                                    type: mssg.payload.fields[0].type,
                                    label: mssg.payload.fields[0].label,
                                    required: true,
                                    actions: mssg.payload.actions
                                }
                                : undefined
                        };
                    });
                } else {
                    agentMessages.push({
                        id: Date.now().toString(),
                        sender: 'agent',
                        type: data.type,
                        text: data.message,
                        actions: data.payload?.actions || [],
                        fields: data.payload?.fields || [],
                        payload: {
                            actions: data.payload?.actions || [],
                            grid: data.payload?.grid,
                            document: data.payload?.document
                        },
                        options: Array.from(new Set([
                            ...(data.metadata?.quickReplies || []),
                        ])),
                        formField: data.type === 'form' && data.payload?.fields?.length > 0 && data.payload.fields[0]
                            ? {
                                name: data.payload.fields[0].name,
                                placeholder: data.payload.fields[0].placeholder,
                                type: data.payload.fields[0].type,
                                label: data.payload.fields[0].label,
                                required: true,
                                actions: data.payload?.actions || []
                            }
                            : undefined
                    });
                }

                // Check for audio_data in metadata and create voice note message
                if (data?.metadata?.media?.audio_data && 
                    typeof data.metadata.media.audio_data === 'string' &&
                    data.metadata.media.audio_data.trim().length > 0) {
                    
                    const audioData = data.metadata.media.audio_data.trim();
                    
                    // Validate that it's a base64 audio data URI (starts with "data:audio/")
                    if (audioData.startsWith('data:audio/')) {
                        const voiceNoteMessage: IMessage = {
                            id: Date.now().toString() + '-audio',
                            sender: 'agent',
                            type: 'audio',
                            text: '',
                            payload: {
                                audioSource: audioData,
                                uploadState: 'success'
                            }
                        };
                        agentMessages.push(voiceNoteMessage);
                    }
                }

                // For end_agent_chat, skip adding HTTP response messages to prevent duplication
                // The WebSocket event (agent_session_closed) will handle the UI update
                if (!isEndAgentChat) {
                    setMessages((prevMessages) => ([...prevMessages, ...agentMessages]));
                } else {
                    console.log('[END_AGENT_CHAT] Skipping HTTP response messages, waiting for WebSocket event');
                }
                
                // Request newer messages after receiving response
                requestNewerMessages();
            }

        } catch (error: any) {
            console.log('[ERROR] handleSendMessage:', error);
            trackUserAction('send_message_error', {
                error: error.message,
                channel
            });
        } finally {
            console.log('[SEND] finalize: stop typing, scrollToBottom');
            setIsTyping(false);
            scrollToBottom();
        }
    };

    // const getConversationHistory = useCallback(async () => {
    //     try {
    //         const response = await trackApiCall('get_conversation_history',
    //             () => chatService.get<IConversationResponse>(apiRoutes.conversation),
    //             { conversationId: conversationId || 'new' }
    //         );

    //         if (response.data.status === 'success') {
    //             trackUserAction('conversation_history_loaded', {
    //                 messageCount: response.data.data.messages.length,
    //                 channel
    //             });
    //             const resData = response.data.data.messages;
    //             const formattedMessages = resData.map((msg: {
    //                 _id: string;
    //                 role: string;
    //                 message: string;
    //                 metadata: {
    //                     quickReplies?: string[];
    //                     userId: string;
    //                     intent: {
    //                         type: string;
    //                         subject: string;
    //                     };
    //                     conversationId: string;
    //                     language: string;
    //                     sentiment?: {
    //                         score: number;
    //                         label: string;
    //                     };
    //                     timestamp: string;
    //                 };
    //             }, index: number) => {
    //                 return ({
    //                     id: msg._id,
    //                     sender: msg.role === 'ai' ? 'agent' : 'customer',
    //                     type: 'message',
    //                     text: msg.message,
    //                     options: index === 0 && msg.role === 'ai' ? msg.metadata.quickReplies : [],
    //                     formField: undefined
    //                 })
    //             });


    //             setMessages(formattedMessages.reverse() as IMessage[]); // Reverse to show oldest first
    //             scrollToBottom();
    //         }
    //     } catch (error: any) {
    //         trackUserAction('conversation_history_error', {
    //             error: error.message,
    //             channel
    //         });
    //     }
    // }, [conversationId, channel, trackApiCall, trackUserAction]);


    useEffect(() => {
        if (conversationId) {
            console.log('[WS] joinConversation:', conversationId);
            webSocketService.joinConversation(conversationId);
        } else {
            console.log('[WS] joinConversation skipped: no conversationId');
        }
    }, [conversationId]);

    useEffect(() => {
        console.log('[WS] registering agent event subscriptions');
        const unsubscribeAssigned = subscribe('agent_assigned', (data: any) => {
            console.log('[WS][recv] "agent_assigned":', data);
            setAgentActive(true);
            const assignedMsg: IMessage = {
                id: Date.now().toString(),
                sender: 'agent',
                type: 'plain',
                text: data?.message || 'Agent has joined the conversation.',
                actions: [
                    { label: 'End Agent Chat', type: 'end_agent_chat', action: 'end_agent_chat' }
                ]
            };
            setMessages(prev => [...prev, assignedMsg]);
            scrollToBottom();
        });

        const unsubscribeAgentMsg = subscribe('agent_message', (data: any) => {
            console.log('[WS][recv] "agent_message":', data);

            const parsed = parseWsMessageEvent(data);
            const msg: IMessage = parsed?.kind === 'response'
                ? createAgentMessageFromResponseData(parsed.responseData, 'agent')
                : {
                    id: Date.now().toString(),
                    sender: 'agent',
                    type: 'plain',
                    text: parsed?.kind === 'text' ? parsed.text : '',
                    actions: [],
                    fields: [],
                    options: []
                };
            const structuredSignature = msg.type === 'structured'
                ? getStructuredSectionsSignature(msg)
                : '';

            setMessages(prev => {
                const recentMessages = prev.slice(-5);
                if (isRecentDuplicateAgentMessage(recentMessages, msg, structuredSignature)) {
                    console.log('[WS][agent_message] Skipping duplicate message:', msg.text);
                    return prev;
                }

                return [...prev, msg];
            });
            scrollToBottom();
        });

        const unsubscribeClosed = subscribe('agent_session_closed', (data: any) => {
            console.log('[WS][recv] "agent_session_closed":', data);
            setAgentActive(false);
            setIsTyping(false);
            
            const closedMsgText = data?.message || 'Agent session ended.';
            
            // Prevent duplicate messages by checking if similar message already exists
            setMessages(prev => {
                // Check if a message with the same text from agent already exists (within last 5 messages)
                const recentMessages = prev.slice(-5);
                const isDuplicate = recentMessages.some(
                    m => m.sender === 'agent' && 
                    (m.text === closedMsgText || m.text?.includes('Agent session') || m.text?.includes('ended'))
                );
                
                if (isDuplicate) {
                    console.log('[WS][agent_session_closed] Skipping duplicate message:', closedMsgText);
                    return prev;
                }
                
                const closedMsg: IMessage = {
                    id: Date.now().toString(),
                    sender: 'agent',
                    type: 'plain',
                    text: closedMsgText
                };
                return [...prev, closedMsg];
            });
            scrollToBottom();
        });

        // Join notification, connection errors, message errors, and reconnect handling
        const unsubscribeJoin = subscribe('joinConversation', (data: any) => {
            console.log('[WS][recv] "joinConversation":', data);
        });
        const unsubscribeConnErr = subscribe('connect_error', (error: any) => {
            console.error('[WS][error] connect_error:', error);
        });
        const unsubscribeMsgErr = subscribe('error', (error: any) => {
            console.error('[WS][error] message error:', error);
        });
        const unsubscribeReconnect = subscribe('reconnect', (data: { attemptNumber: number }) => {
            console.log('[WS] reconnect:', data?.attemptNumber);
            // Request newer messages on reconnect
            requestNewerMessages();
        });

        const unsubscribeConnect = subscribe('connect', (data: any) => {
            console.log('[WS][recv] "connect":', data);
            // Request newer messages on fresh connect
            requestNewerMessages();
        });

        return () => {
            console.log('[WS] unsubscribing agent and connection events');
            unsubscribeAssigned();
            unsubscribeAgentMsg();
            unsubscribeClosed();
            unsubscribeJoin();
            unsubscribeConnErr();
            unsubscribeMsgErr();
            unsubscribeReconnect();
            unsubscribeConnect();
        };
    }, [subscribe, requestNewerMessages]);

    useEffect(() => {
        const initializeChat = async () => {
            try {
                const isMyMtnApp = isAppIntegrationRoute();
                const isEmbed = isEmbedRoute();
                const channelParam = getUrlParam('channel');
                trackUserAction('chat_initialization_start', {
                    channel,
                    isMyMtnApp,
                    isEmbed,
                    source: getUrlParam('source') || undefined
                });

                if (isMyMtnApp) {
                    setChannel('MyMTNAPP');
                } else if (isEmbed && channelParam) {
                    setChannel(isAppChannelParam(channelParam) ? 'MyMTNAPP' : 'Web');
                }

                const source = getUrlParam('source');
                if (source) {
                    persistChatSource(source);
                }

                const firstName = getUrlParam('firstName') || splitName(getUrlParam('name')).firstName;
                const lastName = getUrlParam('lastName') || splitName(getUrlParam('name')).lastName;
                const phone = getUrlParam('phone');

                if (firstName || lastName || phone) {
                    setUserMetaData({
                        firstName: firstName || '',
                        lastName: lastName || '',
                        phone: phone || ''
                    });
                }

                // Loading is covered by the existing init TypingIndicator.
                // Errors/empty payloads silently fall back to the default promo.
                const introContent = await resolveIntroContent();
                introContentRef.current = introContent;
                setMessages(createInitialMessages(firstName || undefined, introContent));
            } finally {
                setIsInitializing(false);
            }
        };

        initializeChat();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);



    const isLastMessageForm = () => {
        if (messages.length === 0) return false;

        const lastFlowAgentMessageIndex = findLastFlowAgentMessageIndex(messages);
        if (lastFlowAgentMessageIndex === -1) return false;

        return messages[lastFlowAgentMessageIndex].type === 'form';
    };

    if (isInitializing) {
        return (
            <div className='chat__widget__body'>
                <div className="chat__widget__body--inner">
                    <div className="chat__widget__body--wrapper">
                        <TypingIndicator />
                    </div>
                </div>
            </div>
        );
    }

    return (
        <>
            <ToastProvider>
                <div className='chat__widget__body' onMouseMove={handleChatMouseMove}>
                    <div className="chat__widget__body--inner">
                        <div className="chat__widget__body--wrapper">
                            {messages.map((message, index) => {
                                const isCompleted = message.text?.split(" ").slice(0, 4).join(" ") === "Just before you go,";
                                return (
                                    <Message
                                        key={message.id}
                                        message={message}
                                        handleSendMessage={handleSendMessage}
                                        handleRetryVoiceUpload={handleRetryVoiceUpload}
                                        isFirstInGroup={index === 0 || messages[index - 1].sender !== message.sender}
                                        isLastInGroup={index === messages.length - 1 || messages[index + 1].sender !== message.sender}
                                        isCompleted={isCompleted}
                                        pausePlaybackSignal={recordingStartSignal}
                                        onGridSubmit={message.id.startsWith(VIEW_BILL_DEMO_GRID_ID_PREFIX) && message.type === 'grid'
                                            ? (label: string) => handleViewBillDemoGridSubmit(label)
                                            : undefined}
                                    />
                                )
                            })}
                            {isTyping && <TypingIndicator />}
                            <div ref={messagesEndRef} />
                        </div>
                    </div>
                    <ToastContainer />

                    <ChatWidgetInput
                        onSendMessage={handleSendMessage}
                        onSendVoiceMessage={handleSendVoiceMessage}
                        onTypingActivity={notifyPeripheralActivity}
                        onVoiceRecordingStart={handleVoiceRecordingStart}
                        onVoiceRecordingCancel={handleVoiceRecordingStart}
                        disabled={isLastMessageForm()}
                    />
                </div>
            </ToastProvider>
        </>
    );
};

export default ChatWidgetBody;
